import React, { useMemo, useState, useEffect } from "react";
import styles from "./Listings.module.css";
import { Pagination, Stack } from "@mui/material";
import ProductCard from "./components/ProductCard";
import SelectableProductCard from "./components/SelectableProductCard";
import { useNavigate } from "react-router-dom";
import { AddIcon } from "../../../../utils/Icons";
import { useSelector } from "react-redux";
import SkeletonLoading from "../../../GlobalComponents/SkeletonLoading/SkeletonLoading";
import cardStyles from "./components/ProductCard.module.css";
import backend from "../../../../api";

export default function Listings() {
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const companyId = user?.companyId;
  const [items, setItems] = useState([]);
  const [isBootLoading, setIsBootLoading] = useState(true);

  // load listings for manufacturer company
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!companyId) return;
        const res = await backend.get(`/api/listings/company/${companyId}`);
        if (!cancelled) setItems(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (e) {
        console.warn("load listings error:", e?.response?.data || e.message);
      } finally {
        if (!cancelled) setIsBootLoading(false);
      }
    })();
    return () => (cancelled = true);
  }, [companyId]);

  const deleteItem = async (id) => {
    if (isBootLoading) return;
    try {
      await backend.delete(`/api/listings/${id}`);
      setItems((arr) => arr.filter((x) => String(x._id) !== String(id)));
    } catch (e) {
      console.warn("delete error:", e?.response?.data || e.message);
    }
  };

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const currentItems = useMemo(
    () => items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [items, currentPage]
  );
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, items.length);
  const totalItems = items.length;

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(Math.max(totalPages, 1));
  }, [totalPages, currentPage]);

  const onPage = (_, num) => {
    if (isBootLoading) return;
    setCurrentPage(num);
  };

  const SKELETON_COUNT = itemsPerPage;

  return (
    <div className={`${styles.page} layoutPadding`} aria-busy={isBootLoading}>
      {!isBootLoading && items.length === 0 ?
        <div className='empty' >
          <h2 className='emptyTitle' >My Listings </h2>
          <p className="emptyPara" > Showcase your products and brand to a global audience while tracking key performance metrics and engagement insights </p>
          <button
            className={`${styles.btn} ${styles.add}`}
            onClick={() => navigate("/listings/add")}
            disabled={isBootLoading}
          >
            <AddIcon height={13} fill="white" /> Add listing
          </button>
        </div>
        :
        <div className={styles.headerRow}>
          <h2 className={styles.title}>My Listings</h2>

          <div
            className={styles.tools}
            style={{ opacity: isBootLoading ? 0.6 : 1, pointerEvents: isBootLoading ? "none" : "auto" }}
          >
            <button
              className={`${styles.btn} ${styles.add}`}
              onClick={() => navigate("/listings/add")}
              disabled={isBootLoading}
            >
              <AddIcon height={13} fill="white" /> Add Items
            </button>
          </div>
        </div>
      }

      <div className={styles.grid}>
        {isBootLoading
          ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <SelectableProductCard key={`sk-${i}`} disabled>
              <div className={cardStyles.card}>
                <div className={cardStyles.imgBox}>
                  <SkeletonLoading length={1} height="100%" width="100%" br="15px" padding="0" />
                </div>
                <div className={cardStyles.body}>
                  <div className={cardStyles.titleRow} style={{ alignItems: "center" }}>
                    <SkeletonLoading length={1} height="14px" width="60%" br="6px" padding="0 6px 0 0" />
                  </div>
                  <SkeletonLoading length={1} height="15px" width="80%" br="6px" padding="6px 6px 0 0" />
                  <ul className={cardStyles.specs} style={{ listStyle: "none", padding: "6px 0 0 0" }}>
                    <SkeletonLoading length={2} height="13px" width="60%" br="6px" padding="2px 0" />
                  </ul>
                </div>
                <div className={cardStyles.footer}>
                  <SkeletonLoading length={1} height="16px" width="22%" br="8px" padding="0" />
                  <SkeletonLoading length={1} height="28px" width="96px" br="10px" padding="0" />
                </div>
              </div>
            </SelectableProductCard>
          ))
          : currentItems.map((p) => (
            <SelectableProductCard key={p._id} product={p} onDelete={() => deleteItem(p._id)}>
              <ProductCard
                product={{
                  id: p._id,
                  brand: p.brand,
                  brandShort: p.brandShort,
                  discount: p.discount,
                  name: p.productName,
                  subtitle: p.description,
                  specs: [
                    p.usage && `Usage: ${p.usage}`,
                    p.dimmable && `Dimmable: ${p.dimmable}`,
                    p.temperature && `Temp: ${p.temperature}`,
                    p.beamAngle && `Beam: ${p.beamAngle}`,
                    p.wattage && `Watt: ${p.wattage}`,
                  ].filter(Boolean),
                  image: p.image || p.images?.[0] || "/assets/images/placeholder.png",
                  price: Number(p.price || 0),
                  available: !!p.available,
                }}
              />
            </SelectableProductCard>
          ))}
      </div>

      {!isBootLoading && items.length !== 0 &&
        <div className={styles.footerRow}>
          <span className={styles.meta}>
            {isBootLoading ? "Loading…" : <>Showing {startItem} to {endItem} of {totalItems} results</>}
          </span>
          <Stack
            spacing={2}
            className={styles.pagination}
            style={{ opacity: isBootLoading ? 0.6 : 1, pointerEvents: isBootLoading ? "none" : "auto" }}
          >
            <Pagination
              count={totalPages}
              page={Math.min(currentPage, Math.max(totalPages, 1))}
              onChange={onPage}
              shape="rounded"
            />
          </Stack>
        </div>
      }

      <img
        src="/assets/images/hero-wave.png"
        alt=""
        aria-hidden="true"
        className={styles.authBg}
        draggable={false}
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
    </div>
  );
}
